import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { customsV2Service } from '@/services'
import { MasterBillSchema, MasterBillStatus } from '@/services/CustomsV2Service'
import { HttpError } from '@/types/errors'

interface DevanTaskState {
  loading: boolean
  error: string | null
  devanTasks: MasterBillSchema[]
  refreshing: boolean
}

interface DevanTaskActions {
  fetchDevanTasks: () => Promise<void>
  changeTaskStatus: (
    masterBillId: number,
    newStatus: MasterBillStatus
  ) => Promise<void>
  setError: (message: string) => void
  clearError: () => void
  refresh: () => Promise<void>
}

type DevanTaskStore = DevanTaskState & DevanTaskActions

const initialState: DevanTaskState = {
  loading: false,
  error: null,
  devanTasks: [],
  refreshing: false
}

export const useDevanTaskStore = create<DevanTaskStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setError: (message: string) => set({ error: message }),
      clearError: () => set({ error: null }),

      // 获取拆柜任务列表
      fetchDevanTasks: async () => {
        set({ loading: true, error: null })

        const { success, data } = await customsV2Service.getMasterBills()

        if (success && data) {
          // 筛选待拆柜和拆柜中的任务
          const devanTasks = data.filter(
            (bill) =>
              bill.status === MasterBillStatus.AWAITING_DEVAN ||
              bill.status === MasterBillStatus.DEVANNING
          )

          set({
            devanTasks,
            loading: false
          })
        }
      },

      // 刷新数据
      refresh: async () => {
        await get().fetchDevanTasks()
      }
    }),
    {
      name: 'devan-task-store'
    }
  )
)

// 导出状态选择器
export const useDevanTaskState = () => {
  const loading = useDevanTaskStore((state) => state.loading)
  const error = useDevanTaskStore((state) => state.error)
  const devanTasks = useDevanTaskStore((state) => state.devanTasks)
  const refreshing = useDevanTaskStore((state) => state.refreshing)

  return {
    loading,
    error,
    devanTasks,
    refreshing
  }
}

// 导出操作选择器
export const useDevanTaskActions = () => {
  const fetchDevanTasks = useDevanTaskStore((state) => state.fetchDevanTasks)
  const changeTaskStatus = useDevanTaskStore((state) => state.changeTaskStatus)
  const setError = useDevanTaskStore((state) => state.setError)
  const clearError = useDevanTaskStore((state) => state.clearError)
  const refresh = useDevanTaskStore((state) => state.refresh)

  return {
    fetchDevanTasks,
    changeTaskStatus,
    setError,
    clearError,
    refresh
  }
}
